<template>
  <div class="exam-detail">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <el-button icon="el-icon-arrow-left" @click="goBack">返回</el-button>
        <h2>{{ examInfo.examName || '试卷详情' }}</h2>
      </div>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-refresh" @click="refreshData">刷新</el-button>
      </div>
    </div>

    <!-- 学生信息卡片 -->
    <div v-if="studentInfo.studentName" class="student-info-card">
      <div class="student-avatar">
        <img v-if="studentInfo.avatar" :src="studentInfo.avatar" :alt="studentInfo.studentName" />
        <i v-else class="el-icon-user"></i>
      </div>
      <div class="student-details">
        <h3>{{ studentInfo.studentName }} ({{ studentInfo.studentNum }})</h3>
        <div class="student-meta">
          <span><i class="el-icon-school"></i> {{ studentInfo.schoolName }}</span>
          <span><i class="el-icon-user"></i> {{ studentInfo.grade }} {{ studentInfo.className }}</span>
          <span v-if="studentInfo.userPhone"><i class="el-icon-phone"></i> {{ studentInfo.userPhone }}</span>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading" element-loading-text="正在加载试卷详情...">
    </div>

    <!-- 试卷内容 -->
    <div v-else-if="examInfo.id" class="exam-content">
      <!-- 得分表 -->
      <div class="score-summary">
        <h3><i class="el-icon-medal"></i> 得分情况</h3>
        <div class="score-cards">
          <div class="score-card total">
            <div class="score-icon">
              <i class="el-icon-trophy"></i>
            </div>
            <div class="score-content">
              <div class="score-number">{{ examInfo.score || 0 }}</div>
              <div class="score-label">总分</div>
            </div>
          </div>
          <div class="score-card percentage">
            <div class="score-icon">
              <i class="el-icon-pie-chart"></i>
            </div>
            <div class="score-content">
              <div class="score-number">{{ scorePercentage }}%</div>
              <div class="score-label">得分率</div>
            </div>
          </div>
          <div class="score-card level">
            <div class="score-icon">
              <i class="el-icon-star-on"></i>
            </div>
            <div class="score-content">
              <div class="score-number">{{ scoreLevel }}</div>
              <div class="score-label">等级</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 试卷内容区域 -->
      <div class="exam-main">
        <div class="exam-left">
          <!-- 试卷图片 -->
          <div class="paper-images">
            <h4><i class="el-icon-picture"></i> 试卷图片</h4>
            <div v-if="paperImages.length > 0" class="image-gallery">
              <div
                v-for="(image, index) in paperImages"
                :key="index"
                class="image-item"
                @click="previewImageWithAnnotations(image, index)"
              >
                <img :src="image" :alt="`试卷图片 ${index + 1}`" />
                <div class="image-overlay">
                  <i class="el-icon-zoom-in"></i>
                  <span v-if="getImageAnnotationCount(index) > 0" class="annotation-count">
                    {{ getImageAnnotationCount(index) }} 个批注
                  </span>
                </div>
              </div>
            </div>
            <div v-else class="empty-images">
              <i class="el-icon-picture"></i>
              <p>暂无试卷图片</p>
            </div>
          </div>
        </div>

        <div class="exam-right">
          <!-- 题目详情 -->
          <div class="question-details">
            <h4><i class="el-icon-edit"></i> 题目详情</h4>
            <div v-if="questions.length > 0" class="questions-list">
              <div
                v-for="(question, index) in questions"
                :key="question.id || index"
                class="question-item"
              >
                <div class="question-header">
                  <div class="question-number">第{{ index + 1 }}题</div>
                  <div class="question-score">
                    <span class="score-got">{{ question.questionScore || 0 }}</span>
                    <span class="score-total">/ {{ question.questionScore || 0 }} 分</span>
                  </div>
                  <div class="question-type">
                    <el-tag :type="getQuestionTypeTag(question.questionType)" size="mini">
                      {{ getQuestionTypeName(question.questionType) }}
                    </el-tag>
                  </div>
                </div>

                <div class="question-content">
                  <div class="question-text">{{ question.questionContent || `题目ID: ${question.questionId}` }}</div>

                  <!-- 题目图片 -->
                  <div v-if="question.questionImage" class="question-image">
                    <img :src="question.questionImage" :alt="`题目图片`" />
                  </div>

                  <!-- 选择题选项 -->
                  <div v-if="question.questionType === 1 && question.questionOptions" class="question-options">
                    <div
                      v-for="(option, optIndex) in parseOptions(question.questionOptions)"
                      :key="optIndex"
                      class="option-item"
                      :class="{
                        'correct': isCorrectOption(option, question.correctAnswer),
                        'selected': isSelectedOption(option, question.answerId),
                        'wrong': isSelectedOption(option, question.answerId) && !isCorrectOption(option, question.correctAnswer)
                      }"
                    >
                      <span class="option-label">{{ String.fromCharCode(65 + optIndex) }}.</span>
                      <span class="option-text">{{ option }}</span>
                      <i v-if="isCorrectOption(option, question.correctAnswer)" class="el-icon-check correct-icon"></i>
                      <i v-if="isSelectedOption(option, question.answerId) && !isCorrectOption(option, question.correctAnswer)" class="el-icon-close wrong-icon"></i>
                    </div>
                  </div>

                  <!-- 学生答案信息 -->
                  <div class="answer-info">
                    <div class="answer-item">
                      <span class="answer-label">学生答案：</span>
                      <span class="answer-text">{{ question.answerContent || question.answerId || '未作答' }}</span>
                    </div>
                    <div class="answer-item">
                      <span class="answer-label">标准答案：</span>
                      <span class="answer-text">{{ question.correctAnswer || '暂无' }}</span>
                    </div>
                    <div class="answer-item">
                      <span class="answer-label">是否正确：</span>
                      <el-tag :type="question.isRight ? 'success' : 'danger'" size="mini">
                        {{ question.isRight ? '正确' : '错误' }}
                      </el-tag>
                    </div>
                    <div class="answer-item">
                      <span class="answer-label">得分：</span>
                      <span class="answer-text">{{ question.answerScore || 0 }} / {{ question.questionScore || 0 }} 分</span>
                    </div>
                    <div class="answer-item">
                      <span class="answer-label">答题时间：</span>
                      <span class="answer-text">{{ question.answerTime || 0 }} 秒</span>
                    </div>
                    <div v-if="question.questionAnalysis" class="answer-item">
                      <span class="answer-label">题目解析：</span>
                      <div class="answer-text analysis-text">{{ question.questionAnalysis }}</div>
                    </div>
                    <div v-if="question.questionKnowledge" class="answer-item">
                      <span class="answer-label">知识点：</span>
                      <span class="answer-text">{{ question.questionKnowledge }}</span>
                    </div>
                    <div v-if="question.questionDifficulty" class="answer-item">
                      <span class="answer-label">题目难度：</span>
                      <span class="answer-text">{{ question.questionDifficulty }}</span>
                    </div>
                    <div v-if="question.aiRecognitionStatus" class="answer-item">
                      <span class="answer-label">AI识别状态：</span>
                      <el-tag :type="getAIStatusTag(question.aiRecognitionStatus)" size="mini">
                        {{ getAIStatusName(question.aiRecognitionStatus) }}
                      </el-tag>
                    </div>
                    <div v-if="question.aiConfidence" class="answer-item">
                      <span class="answer-label">AI置信度：</span>
                      <span class="answer-text">{{ question.aiConfidence }}%</span>
                    </div>
                    <div v-if="question.teacherComment" class="answer-item">
                      <span class="answer-label">教师评语：</span>
                      <div class="answer-text comment-text">{{ question.teacherComment }}</div>
                    </div>
                  </div>
                </div>

                <!-- 知识点和评语 -->
                <div class="question-footer">
                  <div v-if="question.knowledgePoints && question.knowledgePoints.length > 0" class="knowledge-points">
                    <span class="label">知识点：</span>
                    <el-tag
                      v-for="kp in question.knowledgePoints"
                      :key="kp.id"
                      size="mini"
                      type="info"
                    >
                      {{ kp.name }}
                    </el-tag>
                  </div>
                  <div v-if="question.teacherComment" class="teacher-comment">
                    <span class="label">老师评语：</span>
                    <span class="comment-text">{{ question.teacherComment }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-questions">
              <i class="el-icon-edit"></i>
              <p>暂无题目信息</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else class="error-state">
      <i class="el-icon-warning"></i>
      <p>试卷信息加载失败</p>
      <el-button type="primary" @click="refreshData">重新加载</el-button>
    </div>

    <!-- 图片预览对话框 -->
    <el-dialog
      title="试卷图片预览"
      :visible.sync="imagePreviewVisible"
      width="90%"
      :before-close="closeImagePreview"
    >
      <div class="image-preview-container">
        <div class="preview-tools">
          <el-button-group>
            <el-button size="mini" @click="zoomIn">放大</el-button>
            <el-button size="mini" @click="zoomOut">缩小</el-button>
            <el-button size="mini" @click="resetZoom">重置</el-button>
          </el-button-group>
          <span class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
        </div>
        <div class="image-wrapper" :style="{ transform: `scale(${zoomLevel})` }">
          <img
            :src="currentPreviewImage"
            :alt="`试卷图片 ${currentImageIndex + 1}`"
            class="preview-image"
            ref="previewImage"
          />
          <!-- 批注层 -->
          <div class="annotation-layer">
            <div
              v-for="annotation in currentImageAnnotations"
              :key="annotation.id"
              class="annotation"
              :style="{
                left: annotation.x + 'px',
                top: annotation.y + 'px',
                color: annotation.color,
                fontSize: annotation.size + 'px'
              }"
            >
              {{ getAnnotationSymbol(annotation.type) }}
            </div>
          </div>
        </div>
      </div>
      <div class="preview-actions">
        <el-button @click="prevImage" :disabled="currentImageIndex === 0">上一张</el-button>
        <span class="image-counter">{{ currentImageIndex + 1 }} / {{ paperImages.length }}</span>
        <el-button @click="nextImage" :disabled="currentImageIndex === paperImages.length - 1">下一张</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { examApi } from '@/api/exam'

export default {
  name: 'ExamDetail',
  data() {
    return {
      loading: false,
      examInfo: {},
      studentInfo: {},
      questions: [],
      paperImages: [],
      imagePreviewVisible: false,
      currentPreviewImage: '',
      currentImageIndex: 0,
      // 批注相关
      annotations: [],
      currentImageAnnotations: [],
      zoomLevel: 1
    }
  },
  computed: {
    scorePercentage() {
      if (!this.examInfo.score || !this.examInfo.totalScore) return 0
      return Math.round((this.examInfo.score / this.examInfo.totalScore) * 100)
    },
    scoreLevel() {
      const percentage = this.scorePercentage
      if (percentage >= 90) return '优秀'
      if (percentage >= 80) return '良好'
      if (percentage >= 70) return '中等'
      if (percentage >= 60) return '及格'
      return '不及格'
    }
  },
  mounted() {
    this.loadExamDetail()
  },
  methods: {
    // 加载试卷详情
    async loadExamDetail() {
      this.loading = true
      try {
        const courseId = this.$route.params.courseId
        const examId = this.$route.params.examId

        // 获取考试基本信息
        const examResponse = await examApi.getExamById(courseId, examId)
        if (examResponse && examResponse.code === 1) {
          this.examInfo = examResponse.data
        }

        // 获取学生答题详情
        try {
          const detailResponse = await examApi.getStudentExamDetail(courseId, examId)
          if (detailResponse && detailResponse.code === 1) {
            const detailData = detailResponse.data

            // 更新考试基本信息
            if (detailData.examId) {
              this.examInfo = {
                ...this.examInfo,
                id: detailData.examId,
                examName: detailData.examName,
                examDescription: detailData.examDescription,
                totalScore: detailData.totalScore,
                duration: detailData.duration,
                examStartTime: detailData.examStartTime,
                examEndTime: detailData.examEndTime,
                examStatus: detailData.examStatus,
                passScore: detailData.passScore,
                examType: detailData.examType,
                paperCount: detailData.paperCount,
                isArchived: detailData.isArchived
              }
            }

            // 设置学生信息
            this.studentInfo = {
              studentId: detailData.studentId,
              studentName: detailData.studentName,
              studentNum: detailData.studentNum,
              schoolName: detailData.schoolName,
              grade: detailData.grade,
              className: detailData.className,
              userName: detailData.userName,
              userRealName: detailData.userRealName,
              avatar: detailData.avatar,
              userPhone: detailData.userPhone,
              email: detailData.email,
              gender: detailData.gender,
              birthday: detailData.birthday,
              parentPhone: detailData.parentPhone
            }

            // 设置题目和图片信息
            this.questions = detailData.questions || []
            this.paperImages = detailData.paperImages || []

            // 更新考试信息中的分数
            if (detailData.finalScore !== undefined) {
              this.examInfo.score = detailData.finalScore
            }

            // 加载批注信息
            await this.loadAnnotations()
          } else {
            // 如果后端接口未实现，使用模拟数据
            this.loadMockData()
          }
        } catch (error) {
          console.warn('获取学生答题详情失败，使用模拟数据:', error)
          this.loadMockData()
        }
      } catch (error) {
        console.error('加载试卷详情失败:', error)
        this.$message.error('加载试卷详情失败')
      } finally {
        this.loading = false
      }
    },

    // 刷新数据
    async refreshData() {
      await this.loadExamDetail()
    },

    // 返回上一页
    goBack() {
      this.$router.go(-1)
    },

    // 加载批注信息
    async loadAnnotations() {
      try {
        if (!this.studentInfo.studentId) {
          console.warn('ExamDetail - 学生ID不存在，无法加载批注信息')
          return
        }

        const courseId = this.$route.params.courseId
        const examId = this.$route.params.examId

        const response = await examApi.getGradingAnnotations(examId, this.studentInfo.studentId)
        if (response && response.code === 1) {
          this.annotations = response.data || []
          console.log('ExamDetail - 加载批注信息成功:', this.annotations.length)
        } else {
          console.warn('ExamDetail - 批注API响应异常:', response)
        }
      } catch (error) {
        console.error('ExamDetail - 加载批注信息失败:', error)
        this.annotations = []
      }
    },

    // 预览图片（带批注）
    previewImageWithAnnotations(image, index) {
      console.log(`ExamDetail - 预览图片${index}: ${image}`)
      this.currentPreviewImage = image
      this.currentImageIndex = index
      this.currentImageAnnotations = this.getImageAnnotations(index)
      console.log(`ExamDetail - 当前图片批注:`, this.currentImageAnnotations)
      this.imagePreviewVisible = true
      this.zoomLevel = 1
    },

    // 预览图片（简单）
    previewImage(image, index) {
      this.currentPreviewImage = image
      this.currentImageIndex = index
      this.imagePreviewVisible = true
    },

    // 关闭图片预览
    closeImagePreview() {
      this.imagePreviewVisible = false
      this.currentImageAnnotations = []
    },

    // 获取指定图片的批注
    getImageAnnotations(imageIndex) {
      return this.annotations.filter(annotation => {
        return Number(annotation.imageIndex) === Number(imageIndex)
      })
    },

    // 获取图片批注数量
    getImageAnnotationCount(imageIndex) {
      return this.getImageAnnotations(imageIndex).length
    },

    // 获取批注符号
    getAnnotationSymbol(type) {
      const symbols = {
        'correct': '✓',
        'partial': '△',
        'wrong': '✗',
        'text': 'T'
      }
      return symbols[type] || '?'
    },

    // 缩放控制
    zoomIn() {
      this.zoomLevel = Math.min(this.zoomLevel * 1.2, 3)
    },

    zoomOut() {
      this.zoomLevel = Math.max(this.zoomLevel / 1.2, 0.5)
    },

    resetZoom() {
      this.zoomLevel = 1
    },

    // 获取题目类型名称
    getQuestionTypeName(type) {
      const typeMap = {
        1: '单选题',
        2: '多选题',
        3: '判断题',
        4: '简答题'
      }
      return typeMap[type] || '未知类型'
    },

    // 获取题目类型标签样式
    getQuestionTypeTag(type) {
      const tagMap = {
        1: 'primary',
        2: 'success',
        3: 'warning',
        4: 'info'
      }
      return tagMap[type] || 'default'
    },

    // 获取AI识别状态名称
    getAIStatusName(status) {
      const statusMap = {
        0: '未识别',
        1: '识别中',
        2: '识别完成',
        3: '识别失败'
      }
      return statusMap[status] || '未知状态'
    },

    // 获取AI识别状态标签样式
    getAIStatusTag(status) {
      const tagMap = {
        0: 'info',
        1: 'warning',
        2: 'success',
        3: 'danger'
      }
      return tagMap[status] || 'default'
    },

    // 解析选项字符串
    parseOptions(optionsStr) {
      if (!optionsStr) return []
      try {
        // 尝试解析JSON格式的选项
        if (optionsStr.startsWith('[') || optionsStr.startsWith('{')) {
          return JSON.parse(optionsStr)
        }
        // 解析逗号分隔的选项
        return optionsStr.split(',').map(opt => opt.trim()).filter(opt => opt)
      } catch (e) {
        console.warn('解析选项失败:', e)
        return []
      }
    },

    // 判断选项是否正确
    isCorrectOption(option, correctAnswer) {
      if (!correctAnswer) return false
      return correctAnswer.includes(option) || correctAnswer === option
    },

    // 判断选项是否被选中
    isSelectedOption(option, studentAnswer) {
      if (!studentAnswer) return false
      return studentAnswer.includes(option) || studentAnswer === option
    },

    // 上一张图片
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--
        this.currentPreviewImage = this.paperImages[this.currentImageIndex]
      }
    },

    // 下一张图片
    nextImage() {
      if (this.currentImageIndex < this.paperImages.length - 1) {
        this.currentImageIndex++
        this.currentPreviewImage = this.paperImages[this.currentImageIndex]
      }
    },

    // 加载模拟数据（用于测试）
    loadMockData() {
      // 模拟试卷图片
      this.paperImages = [
        'https://via.placeholder.com/800x600/4CAF50/FFFFFF?text=试卷第1页',
        'https://via.placeholder.com/800x600/2196F3/FFFFFF?text=试卷第2页',
        'https://via.placeholder.com/800x600/FF9800/FFFFFF?text=试卷第3页'
      ]

      // 模拟题目数据
      this.questions = [
        {
          id: 1,
          type: 1, // 选择题
          content: '下列哪个选项是正确的？',
          score: 10,
          studentScore: 10,
          options: [
            { content: '选项A', isCorrect: true, isSelected: true },
            { content: '选项B', isCorrect: false, isSelected: false },
            { content: '选项C', isCorrect: false, isSelected: false },
            { content: '选项D', isCorrect: false, isSelected: false }
          ],
          knowledgePoints: [
            { id: 1, name: '基础概念' },
            { id: 2, name: '数学运算' }
          ],
          teacherComment: '回答正确，思路清晰'
        },
        {
          id: 2,
          type: 2, // 填空题
          content: '请填写正确答案：2 + 3 = ___',
          score: 5,
          studentScore: 5,
          studentAnswer: '5',
          correctAnswer: '5',
          knowledgePoints: [
            { id: 3, name: '加法运算' }
          ],
          teacherComment: '完全正确'
        },
        {
          id: 3,
          type: 3, // 简答题
          content: '请简述数学的重要性。',
          score: 15,
          studentScore: 12,
          studentAnswer: '数学是一门基础学科，它帮助我们理解世界，培养逻辑思维能力，在日常生活和科学研究中都有重要作用。',
          correctAnswer: '数学是研究数量、结构、变化以及空间等概念的一门学科，是自然科学的基础，也是现代科学技术的重要工具。',
          knowledgePoints: [
            { id: 4, name: '数学认识' }
          ],
          teacherComment: '回答基本正确，但可以更加详细和准确'
        }
      ]

      // 模拟考试分数
      this.examInfo.score = 27
      this.examInfo.totalScore = 30
    }
  }
}
</script>

<style scoped>
.exam-detail {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid #e4e7ed;
  margin-bottom: 20px;
}

/* 学生信息卡片 */
.student-info-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  margin-bottom: 20px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.student-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.student-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.student-avatar i {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background: rgba(255, 255, 255, 0.2);
}

.student-details h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
}

.student-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.student-meta span {
  display: flex;
  align-items: center;
  font-size: 14px;
  opacity: 0.9;
}

.student-meta i {
  margin-right: 5px;
  font-size: 16px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-left h2 {
  margin: 0;
  color: #303133;
  font-size: 20px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* 得分表 */
.score-summary {
  margin-bottom: 30px;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.score-summary h3 {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 18px;
  display: flex;
  align-items: center;
}

.score-summary h3 i {
  margin-right: 8px;
  color: #409EFF;
}

.score-cards {
  display: flex;
  gap: 20px;
}

.score-card {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-left: 4px solid #409EFF;
}

.score-card.total {
  border-left-color: #67c23a;
}

.score-card.percentage {
  border-left-color: #409EFF;
}

.score-card.level {
  border-left-color: #e6a23c;
}

.score-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #409EFF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: #fff;
  font-size: 20px;
}

.score-card.total .score-icon {
  background: #67c23a;
}

.score-card.level .score-icon {
  background: #e6a23c;
}

.score-content {
  flex: 1;
}

.score-number {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 5px;
}

.score-label {
  font-size: 14px;
  color: #909399;
}

/* 试卷主要内容 */
.exam-main {
  display: flex;
  gap: 20px;
}

.exam-left {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.exam-right {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.paper-images h4,
.question-details h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.paper-images h4 i,
.question-details h4 i {
  margin-right: 8px;
  color: #409EFF;
}

/* 图片画廊 */
.image-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.image-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.image-item:hover {
  transform: scale(1.05);
}

.image-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-item:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  color: #fff;
  font-size: 24px;
}

.empty-images,
.empty-questions {
  text-align: center;
  padding: 40px;
  color: #909399;
}

.empty-images i,
.empty-questions i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #c0c4cc;
}

/* 题目列表 */
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.question-number {
  font-weight: bold;
  color: #303133;
  font-size: 16px;
}

.question-score {
  font-size: 14px;
  color: #606266;
}

.score-got {
  font-weight: bold;
  color: #67c23a;
  font-size: 16px;
}

.score-total {
  color: #909399;
}

.question-content {
  margin-bottom: 15px;
}

.question-text {
  font-size: 14px;
  line-height: 1.6;
  color: #303133;
  margin-bottom: 15px;
}

/* 选择题选项 */
.question-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
}

.option-item.correct {
  background: #f0f9ff;
  border-color: #67c23a;
}

.option-item.selected {
  background: #fff7e6;
  border-color: #e6a23c;
}

.option-item.wrong {
  background: #fef0f0;
  border-color: #f56c6c;
}

.option-label {
  font-weight: bold;
  margin-right: 8px;
  min-width: 20px;
}

.option-text {
  flex: 1;
  font-size: 14px;
}

.correct-icon {
  color: #67c23a;
  margin-left: 8px;
}

.wrong-icon {
  color: #f56c6c;
  margin-left: 8px;
}

/* 答案区域 */
.question-answers {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.answer-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.answer-label {
  font-weight: bold;
  color: #606266;
  min-width: 80px;
  font-size: 14px;
}

.answer-text {
  flex: 1;
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
}

.long-answer {
  white-space: pre-wrap;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

/* 题目底部信息 */
.question-footer {
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.knowledge-points,
.teacher-comment {
  margin-bottom: 10px;
}

.knowledge-points .label,
.teacher-comment .label {
  font-weight: bold;
  color: #606266;
  margin-right: 8px;
  font-size: 13px;
}

.knowledge-points {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

.comment-text {
  font-size: 13px;
  color: #303133;
  line-height: 1.5;
}

/* 图片预览 */
.image-preview-container {
  position: relative;
  max-height: 70vh;
  overflow: auto;
}

.preview-tools {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
}

.zoom-level {
  font-size: 14px;
  color: #606266;
  font-weight: bold;
}

.image-wrapper {
  position: relative;
  display: inline-block;
  transform-origin: top left;
}

.preview-image {
  max-width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.annotation-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.annotation {
  position: absolute;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  cursor: pointer;
  transition: transform 0.2s;
}

.annotation:hover {
  transform: scale(1.2);
}

.preview-actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.image-counter {
  font-size: 16px;
  color: #606266;
  font-weight: 500;
}

/* 批注相关样式 */
.annotation-count {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(64, 158, 255, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
}

/* 错误状态 */
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.error-state i {
  font-size: 64px;
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-state p {
  font-size: 16px;
  margin-bottom: 20px;
}

/* 加载状态 */
.loading-container {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 题目图片样式 */
.question-image {
  margin: 15px 0;
  text-align: center;
}

.question-image img {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 选项样式 */
.question-options {
  margin: 15px 0;
}

.option-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  margin: 8px 0;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  background: #fff;
  transition: all 0.3s ease;
}

.option-item:hover {
  border-color: #409EFF;
}

.option-item.correct {
  background: #f0f9ff;
  border-color: #67c23a;
  color: #67c23a;
}

.option-item.selected {
  background: #e6f7ff;
  border-color: #409EFF;
}

.option-item.wrong {
  background: #fef0f0;
  border-color: #f56c6c;
  color: #f56c6c;
}

.option-label {
  font-weight: bold;
  margin-right: 10px;
  min-width: 20px;
}

.option-text {
  flex: 1;
}

.correct-icon {
  color: #67c23a;
  margin-left: 8px;
}

.wrong-icon {
  color: #f56c6c;
  margin-left: 8px;
}

/* 解析和评语样式 */
.analysis-text, .comment-text {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #409EFF;
  white-space: pre-wrap;
  line-height: 1.6;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .exam-main {
    flex-direction: column;
  }

  .score-cards {
    flex-direction: column;
  }

  .image-gallery {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  .question-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
